import moment, { Moment } from 'moment'
import React, { useEffect, useRef, useState } from 'react'

import { TypographySecondary } from 'ui/typography/TypographySecondary'
import Calendar from '../components/Calendar'
import DateInput from '../components/DateInput'
import Portal from '../components/Portal'
import Presets from '../components/Presets'
import { PLACEHOLDERS } from '../constants/constants'
import { useDropdownPosition, usePresets } from '../hooks'
import { DateRangePickerProps, PresetType } from '../types'
import { formatDateRangeForInput } from '../utils/dateFormatters'
import { isDateAvailable } from '../utils/dateValidation'
import {
	DropdownContainer,
	InputContainer,
	PresetsContainer,
	ResetButton,
	StyledDateRangePicker,
} from './DateRangePicker.styles'

/**
 * Компонент DateRangePicker для выбора диапазона дат
 * Объединяет DateInput, Calendar и Presets
 * Совместим с API существующего RangeDate компонента
 */
const DateRangePicker: React.FC<DateRangePickerProps> = ({
	startDate,
	endDate,
	onChangeUpdateDate,
	disabled = false,
	restrictionType,
	disabledDates,
	todayFilter = false,
	yesterdayFilter = false,
	weekFilter = false,
	monthFilter = false,
	yearFilter = false,
	isNeedFutureDates = false,
	dayBefore,
	isShowResetBtn = true,
	resetButtonPosition = 'right',
	currentRange,
	onPresetSelect,
	presetResetKey,
	middleContent,
}) => {
	const getPresetByRangeLabel = (range?: string): PresetType | null => {
		if (!range) return null

		const presetMap: Record<string, PresetType> = {
			Сегодня: 'today',
			Вчера: 'yesterday',
			Неделя: 'week',
			Месяц: 'month',
			Год: 'year',
		}

		return presetMap[range] ?? null
	}

	const [isOpen, setIsOpen] = useState(false)

	// Сохраняем начальные значения для сравнения
	const initialStartDate = useRef(startDate)
	const initialEndDate = useRef(endDate)

	const [selectedRange, setSelectedRange] = useState<
		[Moment | null, Moment | null]
	>([startDate ? moment(startDate) : null, endDate ? moment(endDate) : null])
	const containerRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLDivElement>(null)
	const dropdownRef = useRef<HTMLDivElement>(null)
	const isFirstPresetReset = useRef(true)
	const skipNextPresetResetClear = useRef(false)

	// Умное позиционирование дропдауна относительно инпута
	const position = useDropdownPosition(isOpen, inputRef, dropdownRef)

	// Хук для управления пресетами
	const { activePreset, selectPreset, clearPreset } = usePresets(
		(start, end) => {
			setSelectedRange([start, end])
			onChangeUpdateDate?.([start.valueOf(), end.valueOf()])
			setIsOpen(false)
		},
		isNeedFutureDates,
		dayBefore,
	)

	// Автоматически выбираем пресет при монтировании, если передан currentRange
	useEffect(() => {
		const preset = getPresetByRangeLabel(currentRange)
		if (preset) {
			selectPreset(preset)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// Синхронизация локального диапазона с внешними пропсами.
	// Нужна для сценариев, когда диапазон меняется извне (например, выбор лактации).
	useEffect(() => {
		setSelectedRange([
			startDate ? moment(startDate) : null,
			endDate ? moment(endDate) : null,
		])
	}, [startDate, endDate])

	useEffect(() => {
		if (isFirstPresetReset.current) {
			isFirstPresetReset.current = false
			return
		}
		if (skipNextPresetResetClear.current) {
			skipNextPresetResetClear.current = false
			return
		}

		clearPreset()
	}, [clearPreset, presetResetKey])

	// Обработка кликов вне компонента для закрытия дропдауна
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false)
			}
		}

		const handleScroll = (event: Event) => {
			// Не закрываем если скролл внутри дропдауна
			if (dropdownRef.current?.contains(event.target as Node)) {
				return
			}
			setIsOpen(false)
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
			window.addEventListener('scroll', handleScroll, true)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			window.removeEventListener('scroll', handleScroll, true)
		}
	}, [isOpen])

	const handleToggleCalendar = () => {
		if (!disabled) {
			setIsOpen(!isOpen)
		}
	}

	const handleSelectRange = (range: [Moment, Moment]) => {
		setSelectedRange(range)
		// Вызываем callback только когда диапазон полностью выбран
		if (range[0] && range[1]) {
			onChangeUpdateDate?.([range[0].valueOf(), range[1].valueOf()])
			clearPreset()
			setIsOpen(false)
		}
	}

	const handleResetToDefault = () => {
		const defaultPreset = getPresetByRangeLabel(currentRange)
		if (defaultPreset) {
			skipNextPresetResetClear.current = true
			onPresetSelect?.()
			selectPreset(defaultPreset)
			return
		}

		// Сбрасываем к начальным значениям
		const resetStart = initialStartDate.current
			? moment(initialStartDate.current)
			: null
		const resetEnd = initialEndDate.current
			? moment(initialEndDate.current)
			: null

		setSelectedRange([resetStart, resetEnd])

		if (resetStart && resetEnd) {
			onChangeUpdateDate?.([resetStart.valueOf(), resetEnd.valueOf()])
		} else {
			onChangeUpdateDate?.([undefined as any, undefined as any])
		}

		clearPreset()
	}

	// Проверка доступности даты с учетом всех ограничений
	const isDateDisabled = (date: Moment): boolean => {
		return !isDateAvailable(date, {
			restrictionType,
			disabledDates,
		})
	}

	// Формирование массива доступных пресетов
	const availablePresets: PresetType[] = []
	if (todayFilter) availablePresets.push('today')
	if (yesterdayFilter) availablePresets.push('yesterday')
	if (weekFilter) availablePresets.push('week')
	if (monthFilter) availablePresets.push('month')
	if (yearFilter) availablePresets.push('year')

	const showPresets = availablePresets.length > 0

	// Форматирование диапазона для отображения в инпуте
	const formattedValue =
		selectedRange[0] && selectedRange[1]
			? formatDateRangeForInput(selectedRange[0], selectedRange[1])
			: undefined

	// Проверяем, отличаются ли текущие значения от начальных
	const hasChangedFromInitial = () => {
		const currentStart = selectedRange[0]?.valueOf()
		const currentEnd = selectedRange[1]?.valueOf()
		const initStart = initialStartDate.current
		const initEnd = initialEndDate.current

		return currentStart !== initStart || currentEnd !== initEnd
	}

	// Показываем кнопку сброса только при кастомном выборе из календаря (не через пресеты)
	const showResetButton =
		isShowResetBtn &&
		activePreset === null &&
		hasChangedFromInitial() &&
		!disabledDates

	return (
		<StyledDateRangePicker ref={containerRef}>
			{showPresets && (
				<PresetsContainer>
					<Presets
						activePreset={activePreset}
						availablePresets={availablePresets}
						onSelectPreset={preset => {
							onPresetSelect?.()
							selectPreset(preset)
						}}
						disabled={disabled}
					/>
				</PresetsContainer>
			)}
			{middleContent}

			{resetButtonPosition === 'left' && showResetButton && (
				<ResetButton>
					<TypographySecondary
						sx={{ cursor: 'pointer' }}
						onClick={handleResetToDefault}
					>
						Сбросить
					</TypographySecondary>
				</ResetButton>
			)}

			<InputContainer>
				<DateInput
					ref={inputRef}
					value={formattedValue}
					placeholder={PLACEHOLDERS.RANGE.join(' — ')}
					isOpen={isOpen}
					isActive={showResetButton}
					disabled={disabled}
					onClick={handleToggleCalendar}
				/>

				<Portal>
					<DropdownContainer
						ref={dropdownRef}
						$isOpen={isOpen}
						$top={position.top}
						$left={position.left}
					>
						<Calendar
							currentDate={selectedRange[0] || moment()}
							selectedRange={selectedRange}
							mode='range'
							onSelectRange={handleSelectRange}
							isDateDisabled={isDateDisabled}
						/>
					</DropdownContainer>
				</Portal>
			</InputContainer>

			{resetButtonPosition !== 'left' && showResetButton && (
				<ResetButton>
					<TypographySecondary
						sx={{ cursor: 'pointer' }}
						onClick={handleResetToDefault}
					>
						Сбросить
					</TypographySecondary>
				</ResetButton>
			)}
		</StyledDateRangePicker>
	)
}

export default DateRangePicker
